/* eslint-disable no-restricted-syntax -- amber token for warning, theme has no warning colour */
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import type { WizardState } from './types';

const WARNING_ICON = '#B45309';

interface Props {
  state: WizardState;
  onRetry: () => void;
}

export default function StepTest({ state, onRetry }: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  if (state.isFinalizing) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center gap-3 py-10"
      >
        <Loader2
          className="h-7 w-7 animate-spin"
          aria-hidden="true"
          style={{ color: getColour('activeText') }}
        />
        <p className="text-sm" style={{ color: getColour('inactiveText') }}>
          {t('publishing-wizard-test-running')}
        </p>
      </div>
    );
  }

  if (state.testResult?.ok) {
    return (
      <div
        role="status"
        className="flex flex-col items-center gap-3 py-10 text-center"
      >
        <CheckCircle2
          className="h-10 w-10"
          aria-hidden="true"
          style={{ color: getColour('success') }}
        />
        <p className="text-sm font-semibold">
          {t('publishing-wizard-test-success')}
        </p>
        <p className="text-xs" style={{ color: getColour('inactiveText') }}>
          {state.finalizedConnection?.account_name}
        </p>
      </div>
    );
  }

  if (state.testResult && !state.testResult.ok) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-6 text-center"
      >
        <AlertCircle
          className="h-10 w-10"
          aria-hidden="true"
          style={{ color: WARNING_ICON }}
        />
        <p className="text-sm font-semibold">
          {t('publishing-wizard-test-failed')}
        </p>
        {state.testResult.message && (
          <p
            className="max-w-md text-xs"
            style={{ color: getColour('inactiveText') }}
          >
            {state.testResult.message}
          </p>
        )}
        <Button
          variant="primary"
          size="small"
          label={t('publishing-wizard-test-retry')}
          onClick={onRetry}
        />
      </div>
    );
  }

  return (
    <p className="text-sm" style={{ color: getColour('inactiveText') }}>
      {t('publishing-wizard-test-pending')}
    </p>
  );
}
